import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { ISetupPromptsResponse } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { groupGeneratedPromptsByTopic } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/utils";
import type { ExplicitLLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { DefaultFetchService } from "app/services/fetchService";

const setupPromptsQueryKey = (
    domain: string,
    cultureName: string,
    topics: string[],
    promptsPerTopic: number,
    chatbots: ExplicitLLModel[],
) => ["llm-tracker", "generate prompts", domain, cultureName, topics ?? [], promptsPerTopic, chatbots];

export const useSetupPrompts = (
    domain: string,
    cultureName: string,
    topics: string[],
    promptsPerTopic: number,
    chatbots: ExplicitLLModel[],
    options?: { enabled?: boolean },
) => {
    const queryClient = useQueryClient();
    const queryKey = setupPromptsQueryKey(domain, cultureName, topics, promptsPerTopic, chatbots);

    const query = useQuery<ISetupPromptsResponse>({
        queryKey,
        queryFn: async ({ signal }) => {
            const fetchService = DefaultFetchService.getInstance();
            const response = await fetchService.post<{
                prompts?: Array<{ topicName: string; query: string; intent: string }>;
            }>(
                "/api/aiTracker/prompts/v2",
                {
                    domain,
                    cultureName,
                    topics,
                    promptsPerTopic,
                    chatbots,
                },
                { cancellation: signal },
            );

            return groupGeneratedPromptsByTopic(response?.prompts);
        },
        retry: 1,
        gcTime: 0,
        staleTime: 0,
        ...options,
    });

    const cancelQuery = () => queryClient.cancelQueries({ queryKey });

    return { ...query, cancelQuery };
};
